import apiClient from '../config/api';

const capaService = {
  /**
   * Get CAPA records with optional filters and pagination
   * @param {number|null} rcaId
   * @param {number|null} statusId
   * @param {number|null} assigneeId
   * @param {object} pagination - { page, pageSize, sortBy, sortOrder }
   * @returns {Promise<{data: Array, pagination: Object}>}
   */
  getCapaRecords: async (rcaId = null, statusId = null, assigneeId = null, pagination = {}) => {
    const params = {};
    if (rcaId) params.rcaId = rcaId;
    if (statusId) params.statusId = statusId;
    if (assigneeId) params.assigneeId = assigneeId;
    if (pagination.page) params.page = pagination.page;
    if (pagination.pageSize) params.pageSize = pagination.pageSize;
    if (pagination.sortBy) params.sortBy = pagination.sortBy;
    if (pagination.sortOrder) params.sortOrder = pagination.sortOrder;

    const response = await apiClient.get('/CapaRecord', { params });
    return response.data;
  },

  /**
   * Get CAPA record including actions
   * @param {number} capaId
   * @returns {Promise<object>}
   */
  getCapaRecordById: async (capaId) => {
    const response = await apiClient.get(`/CapaRecord/${capaId}`);
    return response.data;
  },

  /**
   * Create CAPA record
   * @param {object} capaData
   * @returns {Promise<object>}
   */
  createCapaRecord: async (capaData) => {
    const response = await apiClient.post('/CapaRecord', capaData);
    return response.data;
  },

  /**
   * Update CAPA record
   * @param {number} capaId
   * @param {object} capaData
   * @returns {Promise<object>}
   */
  updateCapaRecord: async (capaId, capaData) => {
    const response = await apiClient.post(`/CapaRecord/update/${capaId}`, capaData);
    return response.data;
  },

  /**
   * Delete CAPA record
   * @param {number} capaId
   * @returns {Promise<object>}
   */
  deleteCapaRecord: async (capaId) => {
    const response = await apiClient.post(`/CapaRecord/delete/${capaId}`);
    return response.data;
  },

  /**
   * Get actions for a CAPA
   * @param {number} capaId
   * @returns {Promise<Array>}
   */
  getCapaActionsByCapa: async (capaId) => {
    const response = await apiClient.get(`/CapaAction/capa/${capaId}`);
    return response.data;
  },

  /**
   * Get all CAPA actions for management view
   * @returns {Promise<Array>}
   */
  getAllCapaActions: async () => {
    const response = await apiClient.get('/CapaAction');
    return response.data;
  },

  /**
   * Get CAPA actions assigned to a user
   * @param {number} assigneeId
   * @returns {Promise<Array>}
   */
  getCapaActionsByAssignee: async (assigneeId) => {
    const response = await apiClient.get(`/CapaAction/assignee/${assigneeId}`);
    return response.data;
  },

  /**
   * Get CAPA action by ID
   * @param {number} actionId
   * @returns {Promise<object>}
   */
  getCapaActionById: async (actionId) => {
    const response = await apiClient.get(`/CapaAction/${actionId}`);
    return response.data;
  },

  /**
   * Create CAPA action (task)
   * @param {object} actionData
   * @returns {Promise<object>}
   */
  createCapaAction: async (actionData) => {
    const response = await apiClient.post('/CapaAction', actionData);
    return response.data;
  },

  /**
   * Update CAPA action
   * @param {number} actionId
   * @param {object} actionData
   * @returns {Promise<object>}
   */
  updateCapaAction: async (actionId, actionData) => {
    const response = await apiClient.post(`/CapaAction/update/${actionId}`, actionData);
    return response.data;
  },

  /**
   * Update CAPA action status
   * @param {number} actionId
   * @param {number} statusId
   * @returns {Promise<object>}
   */
  updateCapaActionStatus: async (actionId, statusId) => {
    const response = await apiClient.post(`/CapaAction/update/${actionId}/status`, { statusId });
    return response.data;
  },

  /**
   * Delete CAPA action
   * @param {number} actionId
   * @returns {Promise<object>}
   */
  deleteCapaAction: async (actionId) => {
    const response = await apiClient.post(`/CapaAction/delete/${actionId}`);
    return response.data;
  },
};

export default capaService;

